import React, { useMemo, useRef, useState } from "react";
import { useQueryWithStore } from "react-admin";
import { Empty, Timeline, Tooltip } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { getTagForChangeTypes, getTagForStates } from "../../Common/Tags";
import { formatDateAndTime, formatDateAndTimeFromNow } from "../../../helpers/utils";
import { spinnerElement } from "../../Common/utils";
import LazyExpandedRow from "../../Common/LazyExpandedRow";
import CollapsePanel from "../../Common/CollapsePanel";
import { /* OpenRowHint, */ useExpandedRow } from "../../Common/OpenCard";
import ChangeInstanceDetailCompact from "./ChangeInstanceDetailCompact";

const stateColor: { [key: string]: string } = {
    PENDING: "#c98a12",
    APPROVED: "#2f6bff",
    COMPLETED: "#15a66a",
    ERROR: "#e0457b",
    REJECTED: "#e07b1a",
    CLOSED: "#8a93a6",
};

const STATE_ORDER = ["PENDING", "APPROVED", "COMPLETED", "ERROR", "REJECTED", "CLOSED"];
const niceState = (s: string) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : s);

/**
 * One change on the service item's timeline. It opens in place by the rules
 * every such row follows (useExpandedRow) — the timeline item it sits in is
 * the row, since antd's Timeline wraps each one.
 */
const ChangeNode = ({
    ci,
    resource,
    open,
    toggle,
    submissionPageRedirectVisible,
}: {
    ci: any;
    resource: string;
    open: boolean;
    toggle: () => void;
    submissionPageRedirectVisible?: boolean;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const headRef = useRef<HTMLDivElement>(null);
    useExpandedRow(ref, headRef, open, { rowSelector: ".ant-timeline-item" });

    return (
        <div ref={ref} className={`ci-node${open ? " ci-node--open" : ""}`}>
            <div
                ref={headRef}
                className="ci-node__head"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle();
                    }
                }}
            >
                <span className="ci-node__main">
                    <span className="ci-node__id">#{ci.id}</span>
                    {getTagForChangeTypes(ci.change_type)}
                    {getTagForStates(ci.state)}
                </span>
                <span className="ci-node__meta">
                    {submissionPageRedirectVisible && ci.submission?.id && (
                        <Link
                            className="ci-node__sub"
                            to={`/submissions/${ci.submission.id}/`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            Submission ↗
                        </Link>
                    )}
                    {ci.created && (
                        <Tooltip title={formatDateAndTime(ci.created)}>
                            <span className="ci-node__time">{formatDateAndTimeFromNow(ci.created)}</span>
                        </Tooltip>
                    )}
                    <RightOutlined className="ci-node__chev" aria-hidden="true" />
                </span>
            </div>

            <CollapsePanel open={open} className="ci-node__body nx-open-row__panel">
                <LazyExpandedRow
                    lightRecord={ci}
                    resource={resource}
                    ExpandedRowComponent={ChangeInstanceDetailCompact}
                    extraProps={{ resource: resource }}
                />
            </CollapsePanel>
        </div>
    );
};

const ChangeInstancesTab = (props: any) => {
    const { baseFilter, resource, submissionPageRedirectVisible } = props;
    const [activeState, setActiveState] = useState<string>("ALL");
    const [openId, setOpenId] = useState<number | null>(null);

    const { data, loaded } = useQueryWithStore({
        type: "getList",
        resource: resource,
        payload: {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "id", order: "desc" },
            filter: { ...baseFilter, light: true },
        },
    });

    const items: any[] = data || [];

    const counts = useMemo(() => {
        const c: { [key: string]: number } = {};
        items.forEach((it: any) => { c[it.state] = (c[it.state] || 0) + 1; });
        return c;
    }, [items]);

    const presentStates = STATE_ORDER.filter((s) => counts[s]);
    const filtered = activeState === "ALL" ? items : items.filter((it: any) => it.state === activeState);

    const toggle = (id: number) => setOpenId((prev) => (prev === id ? null : id));

    if (!loaded) return spinnerElement;

    return (
        <div className="ci-tab">
            <div className="ci-filters" role="group" aria-label="Filter change instances by state">
                <button
                    type="button"
                    className={`ci-chip${activeState === "ALL" ? " ci-chip--active" : ""}`}
                    aria-pressed={activeState === "ALL"}
                    onClick={() => setActiveState("ALL")}
                >
                    All <span className="ci-chip__count">{items.length}</span>
                </button>
                {presentStates.map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={`ci-chip${activeState === s ? " ci-chip--active" : ""}`}
                        aria-pressed={activeState === s}
                        onClick={() => setActiveState(s)}
                    >
                        <span className="ci-chip__dot" style={{ background: stateColor[s] || "#8a93a6" }} />
                        {niceState(s)} <span className="ci-chip__count">{counts[s]}</span>
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <Empty className="sid-empty" description="No change instances" />
            ) : (
                <>
                    {/* the circles over the changes either side of an open one.
                        Off now: the list scrolls freely around an open row. OpenRowHint stays, to bring back. */}
                    {/* <OpenRowHint edge="top" noun="change" /> */}
                    <Timeline
                        className="ci-timeline"
                        items={filtered.map((ci: any) => ({
                            color: stateColor[ci.state] || "#8a93a6",
                            children: (
                                <ChangeNode
                                    ci={ci}
                                    resource={resource}
                                    open={openId === ci.id}
                                    toggle={() => toggle(ci.id)}
                                    submissionPageRedirectVisible={submissionPageRedirectVisible}
                                />
                            ),
                        }))}
                    />
                    {/* <OpenRowHint edge="bottom" noun="change" /> */}
                </>
            )}
        </div>
    );
};

export default ChangeInstancesTab;
